import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-6xl',
};

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  size?: ModalSize;
  footer?: React.ReactNode;
  children: React.ReactNode;
  showCloseButton?: boolean;
  closeOnOverlay?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  bodyClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  /** Custom header replaces title/subtitle row */
  header?: React.ReactNode;
  /** Outer overlay wrapper (default z-50). Use z-[60] for nested dialogs. */
  zClass?: string;
}

export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children,
  showCloseButton = true,
  closeOnOverlay = true,
  closeOnEscape = true,
  className = '',
  bodyClassName = '',
  headerClassName = '',
  footerClassName = '',
  header,
  zClass = 'z-50',
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const hasDefaultHeader = Boolean(header || title || showCloseButton);

  return (
    <div className={`fixed inset-0 ${zClass} overflow-y-auto`}>
      {/* Phone: sheet inferior; md+ (iPad/desktop): dialog centrado */}
      <div className="flex min-h-[100dvh] items-end justify-center p-0 sm:items-center sm:p-4 md:p-6">
        <div
          className="fixed inset-0 bg-slate-950/50 transition-opacity"
          onClick={closeOnOverlay ? onClose : undefined}
          aria-hidden
        />

        <div
          role="dialog"
          aria-modal="true"
          className={`relative flex w-full ${SIZE_CLASSES[size]} max-h-[92dvh] sm:max-h-[90dvh] flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-xl dark:border-gray-700 dark:bg-gray-800 ${className}`}
        >
          {hasDefaultHeader && (
            <div
              className={`flex flex-shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-6 sm:py-4 md:px-6 dark:border-gray-700 ${headerClassName}`}
            >
              {header ?? (
                <div className="min-w-0 flex-1">
                  {title != null && (
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                      {title}
                    </h3>
                  )}
                  {subtitle != null && (
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {subtitle}
                    </div>
                  )}
                </div>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="panel-touch flex-shrink-0 rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-700 dark:hover:text-slate-200"
                  aria-label={t('common.close')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <div className={`min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 md:px-6 ${bodyClassName}`}>
            {children}
          </div>

          {footer != null && (
            <div
              className={`flex flex-shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 panel-safe-bottom sm:flex-row sm:justify-end sm:gap-3 sm:px-6 dark:border-gray-700 dark:bg-gray-900/50 ${footerClassName}`}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
